/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The shared-recipe strip's rendering decision, as a pure function so it can
 * be tested without a DOM (the house pattern: node --test over pure logic,
 * like lib/dates.test.ts).
 *
 * The shelf is the library-recipes list fetched with includeOwn=true: other
 * members' shares first, then the reader's own, flagged isOwn. Three states:
 *
 *   empty   — nobody shares anything: the dashed "Share yours" line.
 *   ownOnly — only the reader's own shares exist. This used to fall into
 *             "empty" because the endpoint excluded the caller's household,
 *             which is how a circle with 29 shared recipes announced "No
 *             recipes shared with this group yet". Not an empty shelf, and
 *             not a call to do the thing already done.
 *   mixed   — others' shares exist (with or without own rows): the shelf.
 */

import type { AvailableLibraryRecipe } from "../../api/types.ts";

export type ShelfState =
  | { kind: "empty" }
  | { kind: "ownOnly"; ownCount: number }
  | { kind: "mixed" };

export function shelfState(recipes: AvailableLibraryRecipe[]): ShelfState {
  if (recipes.length === 0) return { kind: "empty" };
  const ownCount = recipes.reduce((n, r) => n + (r.isOwn ? 1 : 0), 0);
  if (ownCount === recipes.length) return { kind: "ownOnly", ownCount };
  return { kind: "mixed" };
}
